import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import nacmeLogo from './assets/nacme_logo.jpg';
import tlaLogo from './assets/tla_logo.png';
import { IS_TLA, PATHS } from './utils/platformPaths';

const AlumniLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store token and user data
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userRole', 'alumni');
      localStorage.setItem('alumniData', JSON.stringify(data.user));

      // Navigate to alumni dashboard (platform-aware)
      navigate(PATHS.dashboard);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className={`mx-auto w-auto ${IS_TLA ? 'h-48 sm:h-60' : 'h-16 sm:h-20'}`}
          src={IS_TLA ? tlaLogo : nacmeLogo}
          alt={IS_TLA ? "TLA Logo" : "NACME Logo"}
        />
        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
          {IS_TLA ? 'TLA Engineer Network' : 'Alumni Portal Login'}
        </h2>
        <p className="mt-2 text-center text-sm sm:text-base text-gray-600 px-2">
          {IS_TLA
            ? 'Access job opportunities and connect with the TLA community'
            : 'Access job opportunities and connect with the NACME network'}
        </p>
      </div>

      <div className="mt-6 sm:mt-8 mx-auto w-full max-w-sm sm:max-w-md">
        <div className="bg-white py-6 px-4 shadow-md rounded-lg sm:py-8 sm:px-10 sm:shadow-lg">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {error}
                    </h3>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base sm:text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div className="text-center sm:text-left">
                <Link to={PATHS.forgotPassword} className="text-sm sm:text-sm font-medium text-blue-600 hover:text-blue-500 py-2 px-1 block sm:inline">
                  Forgot your password?
                </Link>
              </div>
              <div className="text-center sm:text-right">
                <Link to={PATHS.register} className="text-sm sm:text-sm font-medium text-blue-600 hover:text-blue-500 py-2 px-1 block sm:inline">
                  Create account
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white ${
                  isLoading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Other portals</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Link
                to="/login"
                className="w-full inline-flex justify-center py-3 sm:py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-base sm:text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                Student Portal
              </Link>
              <Link
                to="/recruiter/login"
                className="w-full inline-flex justify-center py-3 sm:py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-base sm:text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              >
                Recruiter Portal
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlumniLogin;